"use client";

import { useState, useRef, useEffect } from "react";
import { LogOut, Settings, BookOpen, Activity } from "lucide-react";
import { signOut } from "next-auth/react";

interface ProfileMenuProps {
    name: string | null;
    image?: string | null;
}

export function ProfileMenu({ name, image }: ProfileMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const initial = name ? name[0].toUpperCase() : "U";

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white shadow-lg hover:scale-105 transition-transform overflow-hidden relative"
            >
                {image ? (
                    // eslint-disable-next-line
                    <img src={image} alt={name || "User"} className="w-full h-full object-cover" />
                ) : (
                    initial
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl py-1 z-50 animate-in fade-in slide-in-from-top-2">
                    <div className="px-4 py-3 border-b border-slate-800">
                        <p className="text-xs text-slate-400">เข้าสู่ระบบโดย</p>
                        <p className="text-sm font-bold text-white truncate">{name || "User"}</p>
                    </div>

                    <a
                        href="/setup"
                        className="w-full text-left px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-800 text-sm flex items-center gap-2 transition-colors"
                    >
                        <Settings size={14} className="text-blue-400" /> ตั้งค่าข้อมูลบุหรี่
                    </a>

                    <a
                        href="/science"
                        className="w-full text-left px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-800 text-sm flex items-center gap-2 transition-colors"
                    >
                        <BookOpen size={14} className="text-emerald-400" /> ข้อมูลทางเทคนิค
                    </a>

                    <a
                        href="/health-report"
                        className="w-full text-left px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-800 text-sm flex items-center gap-2 transition-colors"
                    >
                        <Activity size={14} className="text-orange-400" /> รายงานสุขภาพ & การฟื้นฟู
                    </a>

                    <a
                        href="/api/logout"
                        className="w-full text-left px-4 py-3 text-red-400 hover:bg-red-500/10 text-sm flex items-center gap-2 transition-colors border-t border-slate-800"
                    >
                        <LogOut size={14} /> ออกจากระบบ
                    </a>
                </div>
            )}
        </div>
    );
}
